import * as React from "react";
import { Ramp } from "../../../../../types";
import { compressImageWithFallback } from "../../../../../services/ImageCompressionService";

// Types
interface ImageDimensions {
  width: number;
  height: number;
}

// Helper: Get image dimensions from base64 string
const getImageDimensions = (imageSrc: string): Promise<ImageDimensions> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = reject;
    img.src = imageSrc;
  });
};

// Helper: Build dimension mismatch warning message
const buildDimensionMismatchWarning = (
  existing: ImageDimensions,
  newDim: ImageDimensions
): string => {
  return (
    `⚠️ WARNING: Image Dimension Mismatch!\n\n` +
    `Existing image: ${existing.width} × ${existing.height}px\n` +
    `New image: ${newDim.width} × ${newDim.height}px\n\n` +
    `Replacing with a different size will BREAK CALIBRATION and require recalibrating all:\n` +
    `• Calibration points\n` +
    `• Spot positions\n` +
    `• Zone boundaries\n` +
    `• Markings\n\n` +
    `This is NOT recommended unless you are starting fresh.\n\n` +
    `Do you really want to continue?`
  );
};

// Helper: Validate image dimensions against existing image
const validateImageDimensions = async (
  newImageSrc: string,
  existingImageSrc: string | undefined
): Promise<boolean> => {
  // No validation needed if no existing image
  if (!existingImageSrc) return true;

  const [newDims, existingDims] = await Promise.all([
    getImageDimensions(newImageSrc),
    getImageDimensions(existingImageSrc),
  ]);

  // Dimensions match - no warning needed
  if (
    newDims.width === existingDims.width &&
    newDims.height === existingDims.height
  ) {
    return true;
  }

  // Dimensions don't match - show warning and get user confirmation
  return window.confirm(buildDimensionMismatchWarning(existingDims, newDims));
};

/**
 * Custom hook for handling ramp reference image uploads with compression and validation.
 *
 * Features:
 * - Automatic image compression (2MB max, 1920px max dimension)
 * - Dimension validation against existing image
 * - Warning dialog for dimension mismatches
 * - Loading state management
 *
 * @param ramp - Current ramp object
 * @param setRamp - Function to update ramp state
 * @returns Object containing upload handler and loading state
 */
export const useImageUpload = (ramp: Ramp, setRamp: (ramp: Ramp) => void) => {
  const [isUploading, setIsUploading] = React.useState(false);

  const handleUpload = React.useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setIsUploading(true);

      try {
        // Step 1: Compress image (server-side with fallback to client-side)
        const base64Image = await compressImageWithFallback(file);

        // Step 2: Validate dimensions (shows warning if needed)
        const isValid = await validateImageDimensions(
          base64Image,
          ramp.reference_image
        );

        if (!isValid) {
          setIsUploading(false);
          return;
        }

        // Step 3: Update local ramp state
        setRamp({
          ...ramp,
          reference_image: base64Image,
        });

        setIsUploading(false);
      } catch (error) {
        console.error("Failed to upload image:", error);
        setIsUploading(false);
      } finally {
        // Reset input to allow re-uploading same file
        event.target.value = "";
      }
    },
    [ramp, setRamp]
  );

  return {
    handleUpload,
    isUploading,
  };
};
